import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageBackground from "@/components/PageBackground";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";

export default function AffiliateApply({ onComplete }) {
  const [params] = useSearchParams();
  const invite = params.get("invite");
  const navigate = useNavigate();
  const [payoutEmail, setPayoutEmail] = useState("");
  const [method, setMethod] = useState("paypal");
  const [website, setWebsite] = useState("");
  const [audience, setAudience] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setPayoutEmail(r.data?.email || "")).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/affiliate/apply", {
        payout_email: payoutEmail,
        payout_method: method,
        website: website || null,
        audience: audience || null,
      });
      toast.success("You're an affiliate! Grab your first tracking link →");
      onComplete?.(data);
      navigate("/affiliate/links");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)]">
      <PageBackground variant="cyan-lav" />
      <div className="max-w-2xl mx-auto px-6 py-16 relative z-10">
        <div className="glass-strong rounded-[28px] p-8 fade-up" data-testid="affiliate-apply">
          <div className="pf-chip pf-chip-cyan w-fit">
            <Sparkles className="w-3 h-3" /> Cyannect Affiliate Program
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-3 text-slate-900 leading-tight">
            Earn on every sale you refer
          </h1>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">
            Share Cyannect coupons with your audience — we track every click and pay a commission on every completed order.
            {invite && <span className="ml-1 pf-chip pf-chip-mint">Invited</span>}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="affiliate-apply-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Payout email</label>
              <input required type="email" value={payoutEmail} onChange={(e) => setPayoutEmail(e.target.value)} className="pf-input w-full px-4 py-3 text-sm" data-testid="aff-apply-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Payout method</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="pf-input w-full px-4 py-3 text-sm" data-testid="aff-apply-method">
                <option value="paypal">PayPal</option>
                <option value="e-transfer">Interac e-Transfer</option>
                <option value="wise">Wise</option>
                <option value="manual">Manual / cheque</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Website / handle (optional)</label>
              <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://your-blog.com" className="pf-input w-full px-4 py-3 text-sm" data-testid="aff-apply-website" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">How will you promote? (optional)</label>
              <textarea rows="3" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Personal finance blog, TikTok reviews, deal community…" className="pf-input w-full px-4 py-3 text-sm" data-testid="aff-apply-audience" />
            </div>
            <button type="submit" disabled={submitting} className="pf-btn-primary py-3 px-6 rounded-full font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="aff-apply-submit">
              {submitting ? "Creating your account…" : "Start earning"} <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-[11px] text-slate-500">By continuing you agree to Cyannect's affiliate terms. Payouts are processed monthly for approved earnings.</p>
          </form>
        </div>
      </div>
    </div>
  );
}
